import { Record } from "immutable";
import {
    ConversationApplicationContext,
    ConversationBookviewLocation,
} from "models/interfaces/conversations/conversation-application-context";
import { BlockRecord } from "models/view-models/block-record";
import NotebookRecord from "models/view-models/notebook-record";
import PageRecord from "models/view-models/page-record";
import PublicationRecord from "../publication-record";
import { match } from "react-router";

const defaultValues: ConversationApplicationContext = {
    currentView: undefined,
};

export default class ConversationApplicationContextRecord
    extends Record(defaultValues)
    implements ConversationApplicationContext
{
    // Do NOT set properties on immutable records due to babel and typescript transpilation issue
    // See https://github.com/facebook/create-react-app/issues/6506

    // -----------------------------------------------------------------------------------------
    // #region Constructor
    // -----------------------------------------------------------------------------------------

    constructor(params?: Partial<ConversationApplicationContext>) {
        if (params == null) {
            params = Object.assign({}, defaultValues);
        }

        super(params);
    }

    // #endregion Constructor

    // -----------------------------------------------------------------------------------------
    // #region Public Methods
    // -----------------------------------------------------------------------------------------

    /**
     * Merges new values into the record and returns a new instance.
     *
     * @param {Partial<Conversation>} values
     */
    public with(
        values: Partial<ConversationApplicationContext>
    ): ConversationApplicationContextRecord {
        return new ConversationApplicationContextRecord(
            Object.assign(this.toJS(), values)
        );
    }

    public getPageId(): number | null {
        if (this.currentView?.type !== "Notebooks") {
            return null;
        }

        const pagePrefix = "page_";
        const pageIdString = this.currentView.location?.page?.id;

        if (pageIdString == null) {
            return null;
        }

        if (!pageIdString.startsWith(pagePrefix)) {
            const pageId = Number(pageIdString);
            return isNaN(pageId) ? null : pageId;
        }

        const pageId = Number(pageIdString.substring(pagePrefix.length));
        return isNaN(pageId) ? null : pageId;
    }

    public buildBookviewContext(
        publication?: PublicationRecord,
        location?: ConversationBookviewLocation,
        tableChapterMatch?: match<{}> | null
    ): ConversationApplicationContextRecord {
        return this.with({
            currentView: {
                type: "Bookview",
                location: {
                    ...location,
                    publication: publication
                        ? {
                              code: publication.code,
                              edition: publication.edition,
                              title: publication.getDisplayTitle(),
                          }
                        : undefined,
                    ...(tableChapterMatch
                        ? {
                              chapter: {
                                  title: `Tables in ${tableChapterMatch?.params["chapterNfpaLabel"]}`,
                              },
                          }
                        : {}),
                },
            },
        });
    }
    public buildNotebookContext(
        notebook?: NotebookRecord,
        page?: PageRecord,
        block?: BlockRecord,
        blockSnippet?: string
    ): ConversationApplicationContextRecord {
        return this.with({
            currentView: {
                type: "Notebooks",
                location: {
                    notebook: notebook
                        ? {
                              id: `nb_${notebook.id}`,
                              title: notebook.name,
                          }
                        : undefined,
                    page: page
                        ? {
                              id: `page_${page.id}`,
                              title: page.name,
                          }
                        : undefined,
                    block: block
                        ? {
                              id: `block_${block.id}`,
                              title: blockSnippet ?? "",
                          }
                        : undefined,
                },
            },
        });
    }
    // #endregion Public Methods
}
